/**
 * @copyright Copyright 2021-2025 Epic Systems Corporation
 * @file One row representing one app in the Homegrown Apps widget
 * @module Epic.AppOrchard.Dashboard.HomegrownAppRow
 */

import { CalendarIcon } from "@chakra-ui/icons";
import { Box, ListItem, UnorderedList, VStack } from "@chakra-ui/react";
import { useAsync } from "@epic/react-async-hook";
import { IDispatch } from "@epic/react-redux-booster";
import {
	AOLink,
	AppCredentialsLink,
	AppStatusTag,
	MarkInternalButton,
	NewVersionLink,
	RequestCustomerAppChangeButton,
	UpdateCustomerAppLicenseButton,
} from "ao/components/Core";
import { getAttestation, updateCustomerAppLicenseState } from "ao/components/Dashboard/data";
import { WidgetActionButton, WidgetAppRow, WidgetTag } from "ao/components/Dashboard/widgetHelpers";
import { ClientApplicationStatus, EnvironmentType, IAttestationModel } from "ao/data";
import { useSiteInfoState } from "ao/state/siteInfo";
import { markAsInternal, updateAppsAfterLoad, updateChangeRequest } from "ao/store-methods/homegrownApps";
import { CustomerAppChangeRequestType, CustomerAppLicense, IHomegrownApp, IUserSecurity } from "ao/types";
import { IHomegrownAppsForOrgResponseModel } from "ao/types/dashboard";
import { isNullOrEmpty } from "ao/utils/helpers";
import { isInFlight } from "ao/utils/useAsyncHelpers";
import { FC, memo, useCallback, useMemo, useState } from "react";
import { BiMessageRoundedDetail } from "react-icons/bi";
import { GiNewShoot } from "react-icons/gi";
import { ImStatsBars } from "react-icons/im";
import { IoSpeedometerOutline } from "react-icons/io5";

function formattedChangeRequest(changeType: CustomerAppChangeRequestType) {
	switch (changeType) {
		case CustomerAppChangeRequestType.NonProdActivation:
			return "be activated for non-production";
		case CustomerAppChangeRequestType.ProdActivation:
			return "be activated for production";
		case CustomerAppChangeRequestType.NonProdCredentialUpdate:
			return "have its credentials updated for non-production";
		case CustomerAppChangeRequestType.ProdCredentialUpdate:
			return "have its credentials updated for production";
		default:
			return "";
	}
}

interface IProps {
	app: IHomegrownApp;
	userSecurity: IUserSecurity;
	baseUrl: string;
	orgId: number;
	orgCurrentEpicVersion: number;
	isLocalJKUWarningEnabled: boolean;
	dispatch: IDispatch;
}

/** One row representing one app in the Homegrown apps widget */
export const HomegrownAppRow: FC<IProps> = memo((props: IProps) => {
	const {
		app,
		userSecurity,
		baseUrl,
		orgId,
		dispatch,
		orgCurrentEpicVersion,
		isLocalJKUWarningEnabled,
	} = props;

	//#region state/handlers
	const isRelatedOrgApp = !!app.owningOrgName;
	const appBaseUrl = userSecurity.isEpicCustomer
		? `${baseUrl}Developer/Edit?AppId=`
		: userSecurity.isEpicAdministrator || userSecurity.isSuperAdministrator
		? `${baseUrl}Developer/Review?AppId=`
		: ""; // no app page for non admin Epic staff yet

	const hasAppUrl = appBaseUrl.length > 0;
	const statsUrl = `${baseUrl}Stats?OrgId=${orgId}&ClientId=${app.prodClientId}`;
	const metricsUrl = `${baseUrl}ApiMetrics?OrgId=${orgId}&ClientId=${app.prodClientId}`;
	const isNewestAppVersion = app.appId === app.newestAppVersion;
	const isLockedDown =
		app.status === ClientApplicationStatus.Internal || app.status === ClientApplicationStatus.Active;
	const [attestation, setAttestation] = useState<IAttestationModel | null>(null);
	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const licenseModel = useMemo(() => new CustomerAppLicense(app.license), [app.license]);
	const hasCreds = useMemo(
		() =>
			licenseModel.HasSavedCredsAnyEnv(
				!isNullOrEmpty(app.nonProdJsonWebKeySetUrl),
				!isNullOrEmpty(app.prodJsonWebKeySetUrl),
			),
		[app.nonProdJsonWebKeySetUrl, app.prodJsonWebKeySetUrl, licenseModel],
	);
	const hasBottomContent =
		!isNewestAppVersion || hasCreds || app.hasApiStatsForOrg || app.hasApiMetricsForOrg;

	const handleMarkInternal = useCallback(() => markAsInternal(dispatch, app.appId, orgId), [
		app.appId,
		dispatch,
		orgId,
	]);

	/** save change request updates to the store */
	const handleUpdateChangeRequest = useCallback(
		(
			appId: number,
			orgId: number,
			changeType: CustomerAppChangeRequestType | null,
			changeComment: string | null,
		) => updateChangeRequest(dispatch, appId, orgId, changeType, changeComment),
		[dispatch],
	);

	const handleUpdateLicense = useCallback(
		(response: IHomegrownAppsForOrgResponseModel) =>
			updateAppsAfterLoad(
				dispatch,
				orgId,
				response.homegrownApps,
				response.orgCurrentEpicVersion,
				response.isLocalJKUWarningEnabledForFeb24,
			),
		[dispatch, orgId],
	);

	const [attestationLoadingState] = useAsync(getAttestation, {
		executeImmediately: siteInfo.isCustomerAppAttestationEnabled,
		displayName: "loadAssociations",
		onSuccess: (response) => {
			setAttestation(response.attestation);
		},
		initialArguments: [app.appId],
	});

	const creationInfo =
		(app.createdByUserName ? `Created by ${app.createdByUserName} on ` : "Created on ") +
		`${app.createdOn.toLocaleDateString()}.`;

	// activations that happened before the dedicated customer license table was created
	// wouldn't have activatedOn or activatedBy populated, but all activations since will have both fields populated

	const nonProdActivationInfo =
		"Activated for non-production" +
		(!isNullOrEmpty(app.license.nonProdLicensedByUserName)
			? ` by ${
					app.license.nonProdLicensedByUserName
			  } on ${app.license.nonProdLicensedOn!.toLocaleDateString()}.`
			: ".");

	const prodActivationInfo =
		"Activated for production" +
		(!isNullOrEmpty(app.license.prodLicensedByUserName)
			? ` by ${
					app.license.prodLicensedByUserName
			  } on ${app.license.prodLicensedOn!.toLocaleDateString()}.`
			: ".");

	const appIsNotActivated = !app.license.isActivatedInNonProd && !app.license.isActivatedInProd;

	const isAttestationLoading = isInFlight(attestationLoadingState);

	//#endregion

	return (
		<>
			<WidgetAppRow
				appId={app.appId}
				appName={app.appName}
				subtitle={isRelatedOrgApp && <Box>Created by {app.owningOrgName}</Box>}
				linkUrl={hasAppUrl ? appBaseUrl + app.appId : undefined}
				linkTitle={hasAppUrl ? "Go to this app's page" : undefined}
				belowLogoContent={<AppStatusTag status={app.status} size="sm"></AppStatusTag>}
				additionalAppInfo={
					<VStack spacing="0.3em" width="max-content">
						<WidgetTag
							w="100%"
							label={"Events"}
							tooltipContent={
								appIsNotActivated ? (
									creationInfo
								) : (
									<UnorderedList>
										<ListItem>{creationInfo}</ListItem>
										{app.license.isActivatedInNonProd && (
											<ListItem>{nonProdActivationInfo}</ListItem>
										)}
										{app.license.isActivatedInProd && (
											<ListItem>{prodActivationInfo}</ListItem>
										)}
									</UnorderedList>
								)
							}
							icon={CalendarIcon}
						></WidgetTag>

						{app.license.changeRequestType !== null && (
							<WidgetTag
								w="100%"
								label="Changes Requested"
								tooltipContent={
									<Box>
										{app.license.changeRequestedByUserName} requested that this app{" "}
										{formattedChangeRequest(app.license.changeRequestType)} on{" "}
										{app.license.changeRequestedOn?.toLocaleDateString()}.
										{app.license.changeRequestComment &&
											app.license.changeRequestComment.length > 0 && (
												<>
													<Box mt="0.5em">
														Here is the comment left by the requestor:{" "}
													</Box>
													<Box mt="0.5em">{app.license.changeRequestComment}</Box>
												</>
											)}
									</Box>
								}
								icon={BiMessageRoundedDetail}
								iconProps={{ h: "16px", w: "16px", marginInlineEnd: "0.25rem" }}
								tooltipProps={{
									w: { base: "65vw", lg: "15vw" },
									maxW: { base: "65vw", lg: "15vw" },
								}}
							></WidgetTag>
						)}
					</VStack>
				}
				bottomContent={
					hasBottomContent && (
						<>
							{!isNewestAppVersion &&
								(hasAppUrl ? (
									<NewVersionLink
										newVersionUrl={appBaseUrl + app.newestAppVersion}
										appId={app.appId}
										newestAppId={app.newestAppVersion!}
										isOwnApp
									/>
								) : (
									<Box title="A newer version of this app exists">
										<GiNewShoot
											style={{ display: "inline-block", marginRight: "4px" }}
											color="green"
										/>
										Newer App Version
									</Box>
								))}
							{hasCreds && (
								<AppCredentialsLink
									epicVersion={orgCurrentEpicVersion}
									isOwnApp={true}
									nonProdJsonWebKeySetUrl={app.nonProdJsonWebKeySetUrl}
									prodJsonWebKeySetUrl={app.prodJsonWebKeySetUrl}
									testJwtSigningPublicKeyThumbprint={
										licenseModel.testJwtSigningPublicKeyThumbprint
									}
									prodJwtSigningPublicKeyThumbprint={
										licenseModel.prodJwtSigningPublicKeyThumbprint
									}
									testClientSecretHash={licenseModel.testClientSecretHash}
									prodClientSecretHash={licenseModel.prodClientSecretHash}
									testPublicKeyBlobIsCSP={licenseModel.testPublicKeyBlobIsCSP}
									prodPublicKeyBlobIsCSP={licenseModel.prodPublicKeyBlobIsCSP}
								/>
							)}
							{app.hasApiStatsForOrg && (
								<AOLink
									url={statsUrl}
									target="_blank"
									title="View stats for API calls this app has made"
								>
									<ImStatsBars
										color="darkgoldenrod"
										style={{ display: "inline", marginRight: "4px" }}
									/>
									API Stats
								</AOLink>
							)}
							{app.hasApiMetricsForOrg && (
								<AOLink
									url={metricsUrl}
									target="_blank"
									title="View performance metrics for API calls this app has made"
								>
									<IoSpeedometerOutline
										color="darkgreen"
										style={{ display: "inline", marginRight: "4px" }}
									/>
									API Performance
								</AOLink>
							)}
						</>
					)
				}
				actions={
					userSecurity.isEpicCustomer && (
						<VStack w="100%" spacing="0.25em">
							{!isLockedDown && !isRelatedOrgApp && (
								<MarkInternalButton
									attestation={attestation}
									isAttestationLoading={isAttestationLoading}
									appStatus={app.status}
									successHandler={handleMarkInternal}
									appId={app.appId}
									buttonAs={WidgetActionButton}
									hasApiContextWarning={app.hasOutOfContextAPIs}
									hasApiContextWarningOverride={app.hasApiContextValidationOverride}
								/>
							)}
							{userSecurity.canActivateClients && (
								<>
									<UpdateCustomerAppLicenseButton
										license={licenseModel}
										appStatus={app.status}
										epicVersion={orgCurrentEpicVersion}
										appUsesOAuth2={app.useOAuth2}
										appConsumerType={app.consumerType}
										appIsConfidentialClient={app.isConfidentialClient}
										envType={EnvironmentType.NonProd}
										updateLicenseApi={updateCustomerAppLicenseState}
										successHandler={handleUpdateLicense}
										buttonAs={WidgetActionButton}
										appJsonWebKeySetUrl={app.nonProdJsonWebKeySetUrl}
										hasApiContextWarning={app.hasOutOfContextAPIs}
										hasApiContextWarningOverride={app.hasApiContextValidationOverride}
										publicKeysAllowed={app.publicKeysAllowed}
										getsAppLicensesFromOrganization_Name={
											app.getsAppLicensesFromOrganization_Name
										}
										attestation={attestation}
										isLocalJKUWarningEnabledForFeb24={isLocalJKUWarningEnabled}
										isRelatedOrgApp={isRelatedOrgApp}
										owningOrgName={app.owningOrgName}
										skipAttestation={false}
										isLoading={isAttestationLoading}
									/>
									{/* Require internal/locked down status + non PRD activation ideally for PRD actions,
										but if this app was already activated in PRD (e.g. from Edit Apps) allow updating PRD license */}
									{((isLockedDown && app.license.isActivatedInNonProd) ||
										app.license.isActivatedInProd) && (
										<UpdateCustomerAppLicenseButton
											license={licenseModel}
											appStatus={app.status}
											epicVersion={orgCurrentEpicVersion}
											appUsesOAuth2={app.useOAuth2}
											appConsumerType={app.consumerType}
											appIsConfidentialClient={app.isConfidentialClient}
											envType={EnvironmentType.Prod}
											updateLicenseApi={updateCustomerAppLicenseState}
											successHandler={handleUpdateLicense}
											buttonAs={WidgetActionButton}
											appJsonWebKeySetUrl={app.prodJsonWebKeySetUrl}
											hasApiContextWarning={app.hasOutOfContextAPIs}
											hasApiContextWarningOverride={app.hasApiContextValidationOverride}
											publicKeysAllowed={app.publicKeysAllowed}
											getsAppLicensesFromOrganization_Name={
												app.getsAppLicensesFromOrganization_Name
											}
											attestation={attestation}
											isLocalJKUWarningEnabledForFeb24={isLocalJKUWarningEnabled}
											isRelatedOrgApp={isRelatedOrgApp}
											owningOrgName={app.owningOrgName}
											skipAttestation={false}
											isLoading={isAttestationLoading}
										/>
									)}
								</>
							)}
							{!app.getsAppLicensesFromOrganization_Name && (
								<RequestCustomerAppChangeButton
									orgId={orgId}
									license={app.license}
									successHandler={handleUpdateChangeRequest}
									userSecurity={userSecurity}
									buttonAs={WidgetActionButton}
								></RequestCustomerAppChangeButton>
							)}
						</VStack>
					)
				}
			></WidgetAppRow>
		</>
	);
});
